import { useState, useEffect } from "react";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import ImageUpload from "../components/ImageUpload";
import MultiImageUpload from "../components/MultiImageUpload";
import SearchableSelect from "../components/SearchableSelect";
import {
  getBrandNames,
  getModelsForBrand,
  getAllowedYears,
  VEHICLE_COLORS_ES,
  OTHER_MODEL_OPTION,
} from "../data/vehicleBrandsMexico";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Edit,
  Trash2,
  Car,
  Loader2,
  MapPin,
  Star,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Clock,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const defaultVehicle = {
  make: "",
  model: "",
  custom_model: false,
  custom_model_name: "",
  year: new Date().getFullYear(),
  trim: "",
  plate_number: "",
  vin: "",
  color: "",
  seats: 5,
  transmission: "automatic",
  fuel_type: "gasoline",
  // Marketplace category — determines which homepage carousel this vehicle
  // appears in. Empty string = let the backend infer from make/model/fuel.
  category: "",
  features: [],
  photos: [],
  location_address: "",
  location_city: "Tijuana",
  delivery_enabled: false,
  delivery_radius_km: 15,
  delivery_fee: 150,
  daily_rate: 650,
  weekly_discount: 10,
  monthly_discount: 20,
  unlimited_mileage: false,
  mileage_limit_km: 200,
  extra_mileage_fee: 0.5,
  instant_book: true,
  min_trip_days: 1,
  max_trip_days: 30,
  description: "",
  registration_doc: "",
  insurance_doc: "",
  vin_photo: "",
  cover_photo_index: 0,
};

const featureOptions = [
  "AC",
  "Bluetooth",
  "USB",
  "CarPlay",
  "Android Auto",
  "Backup Camera",
  "Cruise Control",
  "Sunroof",
  "Leather Seats",
  "GPS",
  "Child Seat",
  "AWD",
  "Heated Seats",
  "Lane Assist",
];

export default function HostListingsPage() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [insuranceState, setInsuranceState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState(defaultVehicle);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVehicles();
    (async () => {
      try {
        const { data } = await api.get("/host/insurance/me");
        setInsuranceState(data);
      } catch {
        /* ignore */
      }
    })();
  }, [user]);

  const fetchVehicles = async () => {
    if (!user?.user_id) return;
    try {
      const response = await api.get(`/vehicles/host/${user.user_id}`);
      setVehicles(response.data || []);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData(vehicle);
    } else {
      setEditingVehicle(null);
      setFormData(defaultVehicle);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation: brand / model / year / color required
    // (matches the required flag on the dropdowns; the browser can't enforce
    // `required` on custom Popover-based selects).
    const trimmedMake = (formData.make || "").trim();
    const trimmedModel = (formData.model || "").trim();
    const trimmedColor = (formData.color || "").trim();
    if (!trimmedMake) {
      toast.error("Selecciona la marca del vehículo");
      return;
    }
    if (!trimmedModel) {
      toast.error("Selecciona el modelo del vehículo");
      return;
    }
    // "Otro" model → host must type the model; listing gets flagged for admin review
    const isOtherModel = trimmedModel === OTHER_MODEL_OPTION;
    const customModelName = (formData.custom_model_name || "").trim();
    if (isOtherModel && customModelName.length < 2) {
      toast.error("Escribe el modelo de tu auto para enviarlo a revisión");
      return;
    }
    const allowedYears = getAllowedYears();
    const minYear = allowedYears[allowedYears.length - 1];
    const maxYear = allowedYears[0];
    const yr = parseInt(formData.year, 10);
    if (!yr || yr < minYear || yr > maxYear) {
      toast.error(`El año debe estar entre ${minYear} y ${maxYear}`);
      return;
    }
    if (!trimmedColor) {
      toast.error("Selecciona el color del vehículo");
      return;
    }

    // Other required text/number fields (native `required` no longer fires
    // because the form uses `noValidate` so custom toast validations win).
    if (!(formData.plate_number || "").trim()) {
      toast.error("Ingresa el número de placas");
      return;
    }
    if (!(formData.location_address || "").trim()) {
      toast.error("Ingresa la dirección de recogida");
      return;
    }
    if (!formData.daily_rate || Number(formData.daily_rate) <= 0) {
      toast.error("Ingresa el precio diario del vehículo");
      return;
    }

    // Client-side validation: min 3 photos
    if (!formData.photos || formData.photos.filter(Boolean).length < 3) {
      toast.error("Sube al menos 3 fotos del vehículo");
      return;
    }
    // Vehicle docs required
    if (!formData.registration_doc || !formData.insurance_doc) {
      toast.error("La tarjeta de circulación y la póliza de seguro son obligatorias");
      return;
    }

    setSubmitting(true);

    // Build the payload: swap the "Otro" sentinel for the host-typed model
    // and flag it so admins review it before publishing.
    const payload = { ...formData };
    if (isOtherModel) {
      payload.model = customModelName;
      payload.custom_model = true;
    } else {
      payload.custom_model = false;
    }
    delete payload.custom_model_name;

    try {
      if (editingVehicle) {
        await api.put(`/vehicles/${editingVehicle.vehicle_id}`, payload);
        toast.success("Vehículo actualizado");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehículo agregado");
      }
      setDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Error al guardar";
      // Insurance gate (HTTP 422 from backend) — show a CTA to upload it
      if (typeof detail === "string" && detail.toLowerCase().includes("seguro multiplataforma")) {
        toast.error(msg, {
          duration: 10000,
          action: {
            label: "Subir seguro",
            onClick: () => { window.location.href = "/host/seguro"; },
          },
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm("¿Estás seguro de eliminar este vehículo?")) return;

    try {
      await api.delete(`/vehicles/${vehicleId}`);
      toast.success("Vehículo eliminado");
      fetchVehicles();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleFeatureToggle = (feature) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const statusConfig = {
    pending: { label: "Pendiente", className: "bg-yellow-100 text-yellow-700" },
    approved: { label: "Aprobado", className: "bg-green-100 text-green-700" },
    rejected: { label: "Rechazado", className: "bg-red-100 text-red-700" },
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="host-listings-page">
      <div className="container-main py-8">
        {/* Insurance status banner */}
        <InsuranceBanner state={insuranceState} />

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave">
            Mis Autos
          </h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => handleOpenDialog()}
                className="btn-primary"
                data-testid="add-vehicle-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Auto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingVehicle ? "Editar Vehículo" : "Agregar Nuevo Vehículo"}
                </DialogTitle>
                <DialogDescription>
                  {editingVehicle
                    ? "Actualiza los datos, fotos y documentos de tu vehículo. Todos los cambios se aplicarán inmediatamente."
                    : "Completa la información de tu auto: datos básicos, precio, fotos y documentos requeridos."}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} noValidate className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Marca *</Label>
                    <SearchableSelect
                      value={formData.make}
                      onChange={(v) =>
                        setFormData({ ...formData, make: v, model: "", custom_model_name: "" })
                      }
                      options={getBrandNames()}
                      placeholder="Selecciona marca"
                      searchPlaceholder="Buscar marca..."
                      emptyMessage="Sin marcas que coincidan."
                      testId="vehicle-make"
                    />
                  </div>
                  <div>
                    <Label>Modelo *</Label>
                    <SearchableSelect
                      value={formData.model}
                      onChange={(v) =>
                        setFormData({ ...formData, model: v, custom_model_name: "" })
                      }
                      options={getModelsForBrand(formData.make)}
                      placeholder="Selecciona modelo"
                      searchPlaceholder="Buscar modelo..."
                      emptyMessage="Sin modelos que coincidan."
                      disabled={!formData.make}
                      disabledMessage="Selecciona una marca primero"
                      testId="vehicle-model"
                    />
                  </div>
                  {formData.model === OTHER_MODEL_OPTION && (
                    <div className="col-span-2">
                      <Label>Especifica el modelo *</Label>
                      <Input
                        value={formData.custom_model_name || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, custom_model_name: e.target.value })
                        }
                        placeholder="Ej. Territory Trend"
                        className="mt-1"
                        data-testid="vehicle-custom-model-input"
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        El equipo de RivoNes revisará este modelo antes de publicar tu anuncio.
                      </p>
                    </div>
                  )}
                  <div>
                    <Label>Año *</Label>
                    <SearchableSelect
                      value={String(formData.year || "")}
                      onChange={(v) =>
                        setFormData({ ...formData, year: parseInt(v, 10) })
                      }
                      options={getAllowedYears().map((y) => String(y))}
                      placeholder="Selecciona año"
                      searchPlaceholder="Buscar año..."
                      testId="vehicle-year"
                    />
                  </div>
                  <div>
                    <Label>Color *</Label>
                    <SearchableSelect
                      value={formData.color}
                      onChange={(v) => setFormData({ ...formData, color: v })}
                      options={VEHICLE_COLORS_ES}
                      placeholder="Selecciona color"
                      searchPlaceholder="Buscar color..."
                      testId="vehicle-color"
                    />
                  </div>
                  <div>
                    <Label>Placas *</Label>
                    <Input
                      value={formData.plate_number}
                      onChange={(e) => setFormData({ ...formData, plate_number: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Asientos</Label>
                    <Input
                      type="number"
                      value={formData.seats}
                      onChange={(e) => setFormData({ ...formData, seats: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Transmisión</Label>
                    <Select
                      value={formData.transmission}
                      onValueChange={(value) => setFormData({ ...formData, transmission: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="automatic">Automático</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Combustible</Label>
                    <Select
                      value={formData.fuel_type}
                      onValueChange={(value) => setFormData({ ...formData, fuel_type: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gasoline">Gasolina</SelectItem>
                        <SelectItem value="diesel">Diésel</SelectItem>
                        <SelectItem value="electric">Eléctrico</SelectItem>
                        <SelectItem value="hybrid">Híbrido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    {/* Determines the homepage carousel this listing appears
                        in. Leaving "Auto" lets the backend infer the bucket
                        from make/model/fuel — covers most listings. Admin can
                        override during approval if needed. */}
                    <Label>Categoría del marketplace</Label>
                    <Select
                      // Radix Select forbids empty-string values on
                      // <SelectItem>. We use "__auto__" as a sentinel for
                      // "let the backend infer" and coerce it back to
                      // undefined at submit time (see handleSubmit).
                      value={formData.category || "__auto__"}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          category: value === "__auto__" ? "" : value,
                        })
                      }
                    >
                      <SelectTrigger className="mt-1" data-testid="vehicle-category-select">
                        <SelectValue placeholder="Auto (se infiere)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__auto__">Auto (se infiere)</SelectItem>
                        <SelectItem value="truck">Camioneta / Pickup</SelectItem>
                        <SelectItem value="suv">SUV</SelectItem>
                        <SelectItem value="compact">Compacto / Hatchback</SelectItem>
                        <SelectItem value="economy">Económico</SelectItem>
                        <SelectItem value="electric">Eléctrico</SelectItem>
                        <SelectItem value="luxury">De lujo</SelectItem>
                        <SelectItem value="van">Van / Familiar</SelectItem>
                        <SelectItem value="offroad">Off-road / 4x4</SelectItem>
                        <SelectItem value="sports">Deportivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <Label>Dirección de recogida *</Label>
                  <Input
                    value={formData.location_address}
                    onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                    placeholder="Ej: Zona Centro, Tijuana"
                    required
                    className="mt-1"
                    data-testid="vehicle-address-input"
                  />
                </div>

                {/* Photos */}
                <div>
                  <Label className="mb-2 block">Fotos del vehículo *</Label>
                  <MultiImageUpload
                    value={(formData.photos || []).map((p, idx) => ({
                      id: `p_${idx}_${String(p).slice(-12)}`,
                      url: p,
                      data: p,
                    }))}
                    onChange={(imgs) =>
                      setFormData({
                        ...formData,
                        photos: imgs.map((i) => i.data || i.url),
                      })
                    }
                    coverIndex={formData.cover_photo_index || 0}
                    onCoverChange={(idx) =>
                      setFormData({ ...formData, cover_photo_index: idx })
                    }
                    minImages={3}
                    maxImages={10}
                    imageType="vehicle_photo"
                  />
                </div>

                {/* Pricing */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Precio diario (MXN) *</Label>
                    <Input
                      type="number"
                      value={formData.daily_rate}
                      onChange={(e) => setFormData({ ...formData, daily_rate: parseFloat(e.target.value) })}
                      required
                      className="mt-1"
                      data-testid="vehicle-price-input"
                    />
                  </div>
                  <div>
                    <Label>Descuento semanal (%)</Label>
                    <Input
                      type="number"
                      value={formData.weekly_discount}
                      onChange={(e) => setFormData({ ...formData, weekly_discount: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Descuento mensual (%)</Label>
                    <Input
                      type="number"
                      value={formData.monthly_discount}
                      onChange={(e) => setFormData({ ...formData, monthly_discount: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Delivery */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="delivery"
                      checked={formData.delivery_enabled}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, delivery_enabled: checked })
                      }
                    />
                    <Label htmlFor="delivery">Ofrecer entrega a domicilio</Label>
                  </div>
                  {formData.delivery_enabled && (
                    <div className="grid grid-cols-2 gap-4 pl-6">
                      <div>
                        <Label>Radio (km)</Label>
                        <Input
                          type="number"
                          value={formData.delivery_radius_km}
                          onChange={(e) =>
                            setFormData({ ...formData, delivery_radius_km: parseInt(e.target.value) })
                          }
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Costo entrega (MXN)</Label>
                        <Input
                          type="number"
                          value={formData.delivery_fee}
                          onChange={(e) =>
                            setFormData({ ...formData, delivery_fee: parseFloat(e.target.value) })
                          }
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Mileage */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="unlimited"
                      checked={formData.unlimited_mileage}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, unlimited_mileage: checked })
                      }
                    />
                    <Label htmlFor="unlimited">Kilometraje ilimitado</Label>
                  </div>
                  {!formData.unlimited_mileage && (
                    <div className="grid grid-cols-2 gap-4 pl-6">
                      <div>
                        <Label>Límite diario (km)</Label>
                        <Input
                          type="number"
                          value={formData.mileage_limit_km}
                          onChange={(e) =>
                            setFormData({ ...formData, mileage_limit_km: parseInt(e.target.value) })
                          }
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Costo extra/km (MXN)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formData.extra_mileage_fee}
                          onChange={(e) =>
                            setFormData({ ...formData, extra_mileage_fee: parseFloat(e.target.value) })
                          }
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Instant Book */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="instant"
                    checked={formData.instant_book}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, instant_book: checked })
                    }
                  />
                  <Label htmlFor="instant">Habilitar reserva instantánea</Label>
                </div>

                {/* Features */}
                <div>
                  <Label className="mb-2 block">Características</Label>
                  <div className="flex flex-wrap gap-2">
                    {featureOptions.map((feature) => (
                      <Badge
                        key={feature}
                        variant={formData.features.includes(feature) ? "default" : "outline"}
                        className={`cursor-pointer ${
                          formData.features.includes(feature)
                            ? "bg-terracotta hover:bg-terracotta-hover"
                            : ""
                        }`}
                        onClick={() => handleFeatureToggle(feature)}
                      >
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label>Descripción</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe tu vehículo..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Vehicle Documents */}
                <div className="border-t pt-6">
                  <h3 className="font-heading font-semibold mb-1">Documentos del vehículo</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Sube los documentos requeridos para aprobar tu listado.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="mb-2 block text-sm">Tarjeta de circulación</Label>
                      <ImageUpload
                        imageType="vehicle_registration"
                        maxImages={1}
                        existingImages={formData.registration_doc ? [{ id: "reg", url: formData.registration_doc, data: formData.registration_doc }] : []}
                        onImageUploaded={(imgs) =>
                          setFormData({ ...formData, registration_doc: imgs[0] || "" })
                        }
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm">Póliza de seguro</Label>
                      <ImageUpload
                        imageType="vehicle_insurance"
                        maxImages={1}
                        existingImages={formData.insurance_doc ? [{ id: "ins", url: formData.insurance_doc, data: formData.insurance_doc }] : []}
                        onImageUploaded={(imgs) =>
                          setFormData({ ...formData, insurance_doc: imgs[0] || "" })
                        }
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm">Foto del VIN</Label>
                      <ImageUpload
                        imageType="vehicle_vin"
                        maxImages={1}
                        existingImages={formData.vin_photo ? [{ id: "vin", url: formData.vin_photo, data: formData.vin_photo }] : []}
                        onImageUploaded={(imgs) =>
                          setFormData({ ...formData, vin_photo: imgs[0] || "" })
                        }
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full btn-primary"
                  data-testid="submit-vehicle-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingVehicle ? "Guardar Cambios" : "Agregar Vehículo"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Vehicles List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
          </div>
        ) : vehicles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((vehicle) => {
              const status = statusConfig[vehicle.status] || statusConfig.pending;
              return (
                <div
                  key={vehicle.vehicle_id}
                  className="card-base p-0 overflow-hidden"
                  data-testid={`vehicle-card-${vehicle.vehicle_id}`}
                >
                  <div className="relative aspect-[4/3]">
                    <img
                      src={vehicle.photos?.[0] || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400"}
                      alt={`${vehicle.make} ${vehicle.model}`}
                      className="w-full h-full object-cover"
                    />
                    <Badge className={`absolute top-3 left-3 ${status.className}`}>
                      {status.label}
                    </Badge>
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-heading font-semibold">
                        {vehicle.make} {vehicle.model} {vehicle.year}
                      </h3>
                      {vehicle.review_count > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-marigold text-marigold" />
                          <span className="text-sm">{vehicle.rating?.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm flex items-center gap-1 mb-3">
                      <MapPin className="w-4 h-4" />
                      {vehicle.location_address}
                    </p>
                    <p className="text-terracotta font-semibold mb-4">
                      ${vehicle.daily_rate?.toLocaleString()} MXN/día
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDialog(vehicle)}
                        className="flex-1"
                        data-testid={`edit-${vehicle.vehicle_id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(vehicle.vehicle_id)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`delete-${vehicle.vehicle_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No tienes autos registrados
            </h3>
            <p className="text-gray-500 mb-6">
              Agrega tu primer auto y comienza a generar ingresos
            </p>
            <Button onClick={() => handleOpenDialog()} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Agregar Auto
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


function InsuranceBanner({ state }) {
  if (!state) return null;
  const s = state.state;
  if (s === "approved_active") return null;  // no banner needed when everything ok

  const cfg = {
    none: {
      icon: Shield,
      color: "from-amber-50 to-orange-50 border-amber-200",
      iconColor: "text-amber-600",
      title: "Necesitas Seguro Multiplataforma",
      body: "Antes de publicar un vehículo nuevo o editar uno existente, sube tu póliza.",
    },
    pending: {
      icon: Clock,
      color: "from-amber-50 to-yellow-50 border-amber-200",
      iconColor: "text-amber-600",
      title: "Seguro en revisión",
      body: "Tu póliza está siendo revisada. Te avisaremos en 24-48 horas.",
    },
    approved_warning: {
      icon: ShieldCheck,
      color: "from-amber-50 to-orange-50 border-amber-300",
      iconColor: "text-amber-600",
      title: `Tu seguro vence en ${state.days_until_expiry ?? "—"} días`,
      body: "Renueva tu póliza antes para que no pausemos tus listados automáticamente.",
    },
    expired: {
      icon: ShieldAlert,
      color: "from-red-50 to-rose-50 border-red-200",
      iconColor: "text-red-600",
      title: "Seguro vencido — listados pausados",
      body: "Sube tu nueva póliza para reactivar de inmediato.",
    },
    rejected: {
      icon: ShieldAlert,
      color: "from-red-50 to-rose-50 border-red-200",
      iconColor: "text-red-600",
      title: "Documentación rechazada",
      body: state.record?.rejection_reason || "Revisa los motivos y vuelve a enviar.",
    },
    fraud_flagged: {
      icon: ShieldAlert,
      color: "from-purple-50 to-fuchsia-50 border-purple-200",
      iconColor: "text-purple-600",
      title: "Documento marcado para revisión",
      body: "Nuestro equipo se pondrá en contacto contigo.",
    },
  }[s];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <div
      className={`bg-gradient-to-br ${cfg.color} border rounded-2xl p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-3`}
      data-testid="host-insurance-banner"
    >
      <Icon className={`w-7 h-7 ${cfg.iconColor} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <h3 className="font-heading font-semibold text-[#111111]">{cfg.title}</h3>
        <p className="text-sm text-neutral-700 mt-0.5">{cfg.body}</p>
      </div>
      <Link
        to="/host/seguro"
        className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-[#111111] hover:bg-neutral-800 text-white text-sm font-semibold shadow-sm whitespace-nowrap"
        data-testid="insurance-banner-cta"
      >
        {s === "rejected" || s === "expired" || s === "approved_warning" ? "Actualizar" : s === "pending" ? "Ver detalles" : "Subir póliza"}
      </Link>
    </div>
  );
}

